import { paths } from '../routes/paths';

const NON_CRITICAL_DEFERRED_ROUTES = new Set([
  paths.signIn,
  paths.signUp,
  paths.public.contact,
]);

const idleCallbacks = new WeakMap<() => void, number>();

const normalizePathname = (pathname: string) => {
  if (!pathname || pathname === '/') {
    return pathname || '/';
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
};

export const shouldDeferNonCriticalScriptsForRoute = (pathname: string): boolean => {
  return NON_CRITICAL_DEFERRED_ROUTES.has(normalizePathname(pathname));
};

export const runAfterPageLoad = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  if (document.readyState === 'complete') {
    callback();
    return () => {};
  }

  const onLoad = () => callback();
  window.addEventListener('load', onLoad, { once: true });

  return () => window.removeEventListener('load', onLoad);
};

export const runWhenBrowserIdle = (
  callback: () => void,
  timeout = 1500,
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  if ('requestIdleCallback' in window) {
    const requestIdleCallbackWindow = window as Window & {
      requestIdleCallback: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number;
      cancelIdleCallback: (handle: number) => void;
    };

    const idleId = requestIdleCallbackWindow.requestIdleCallback(() => callback(), { timeout });
    idleCallbacks.set(callback, idleId);

    return () => {
      const activeIdleId = idleCallbacks.get(callback);
      if (activeIdleId !== undefined) {
        requestIdleCallbackWindow.cancelIdleCallback(activeIdleId);
      }
    };
  }

  const timerId = globalThis.setTimeout(callback, 1);
  return () => globalThis.clearTimeout(timerId);
};

export const runAfterPageIsInteractive = (
  callback: () => void,
  idleTimeout = 1500,
): (() => void) => {
  let clearLoadListener: () => void = () => {};
  let clearIdleTask: () => void = () => {};

  clearLoadListener = runAfterPageLoad(() => {
    clearIdleTask = runWhenBrowserIdle(callback, idleTimeout);
  });

  return () => {
    clearLoadListener();
    clearIdleTask();
  };
};
